/**
 * The queue strip: a row of chips for files selected/dropped together.
 *
 * Hidden entirely while only one file is open, so opening a single DXF looks
 * exactly as it did before multi-file queuing existed. It appears — chips,
 * "De-dupe and Export" and "Next file" together — the moment a second file
 * is added via Open a DXF… or a drop, and disappears again if the queue is
 * ever back down to one.
 */

export interface QueueEntryView {
  readonly name: string;
  readonly status: "queued" | "processing" | "review" | "exported" | "error";
  readonly partCount: number | null;
  /** Whether this file's own parts contain a same-shape duplicate, once split. */
  readonly hasDuplicateParts: boolean;
  readonly errorMessage: string | null;
}

export interface QueueStripCallbacks {
  /**
   * Any row but the active one was clicked; jump straight to it. Valid at
   * any status — reviewing files 2-5 first and then returning to file 1 to
   * configure and export it is a normal use of the queue, not just a forward
   * march. Landing back on an already-split file re-splits it fresh, the
   * same as opening it for the first time.
   */
  readonly onSelect: (index: number) => void;
  /** The row's close icon was clicked; drop that entry from the queue. */
  readonly onClose: (index: number) => void;
  readonly onNext: () => void;
  readonly onCombinedExport: () => void;
}

export class QueueStrip {
  private readonly strip: HTMLElement;
  private readonly list: HTMLUListElement;
  private readonly nextButton: HTMLButtonElement;
  private readonly combinedExportButton: HTMLButtonElement;

  constructor(
    private readonly root: Document,
    private readonly callbacks: QueueStripCallbacks,
  ) {
    this.strip = must(root, "queue-strip");
    this.list = must(root, "queue-list") as HTMLUListElement;
    this.nextButton = must(root, "queue-next") as HTMLButtonElement;
    this.combinedExportButton = must(root, "queue-combined-export") as HTMLButtonElement;
    this.nextButton.addEventListener("click", () => this.callbacks.onNext());
    this.combinedExportButton.addEventListener("click", () => this.callbacks.onCombinedExport());
  }

  /**
   * Whether entitlement actually allows a combined export is a question for
   * whoever handles the click, not this component: the button itself is
   * shown to every plan once there is something to combine, so a Day pass or
   * Free account discovers the Pro-only restriction by clicking it rather
   * than never seeing it at all.
   */
  render(entries: readonly QueueEntryView[], activeIndex: number): void {
    const showTray = entries.length > 1;
    this.strip.hidden = !showTray;
    this.nextButton.hidden = !showTray;
    this.combinedExportButton.hidden = !showTray;
    this.list.replaceChildren();

    const fragment = this.root.createDocumentFragment();
    entries.forEach((entry, index) => {
      const row = this.root.createElement("li");
      row.className = `queue-item queue-item--${entry.status}`;
      row.classList.toggle("is-active", index === activeIndex);

      const name = this.root.createElement("span");
      name.className = "queue-item__name";
      name.textContent = entry.name;
      name.title = entry.name;

      const status = this.root.createElement("span");
      status.className = "queue-item__status";
      status.textContent = statusLabel(entry);
      if (entry.errorMessage !== null) status.title = entry.errorMessage;

      const close = this.root.createElement("button");
      close.type = "button";
      close.className = "queue-item__close";
      close.setAttribute("aria-label", `Close ${entry.name}`);
      close.textContent = "×";
      close.addEventListener("click", (event) => {
        event.stopPropagation();
        this.callbacks.onClose(index);
      });

      row.append(name, status, close);

      // Every row but the active one can be jumped to, regardless of status —
      // going back to reconfigure or re-export an already-split file is as
      // valid as jumping ahead to one still queued.
      if (index !== activeIndex) {
        row.classList.add("is-clickable");
        row.addEventListener("click", () => this.callbacks.onSelect(index));
      }

      fragment.append(row);
    });
    this.list.append(fragment);

    this.nextButton.disabled = !entries.some((e, i) => i > activeIndex && e.status === "queued");
  }
}

function statusLabel(entry: QueueEntryView): string {
  switch (entry.status) {
    case "queued":
      return "Queued";
    case "processing":
      return "Processing…";
    case "review":
      return entry.partCount === null
        ? "Ready"
        : entry.partCount === 1
          ? "1 part"
          : `${entry.partCount} parts`;
    case "exported":
      return "Exported";
    case "error":
      return "Error";
  }
}

function must(root: Document, id: string): HTMLElement {
  const element = root.getElementById(id);
  if (element === null) throw new Error(`The page is missing #${id}.`);
  return element;
}
